import { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import type { Conversation } from "@agentic_conversation/api/conversation";
import { SidebarNavItem } from "@agentic_conversation/components/sidebar-nav-item";
import { Button } from "@ui";

interface ConversationsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  conversations: Conversation[];
  currentConversationId?: number;
  onSelectConversation: (id: number) => void;
  onNewConversation: () => void;
}

export function ConversationsDrawer({
  isOpen,
  onClose,
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
}: ConversationsDrawerProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle mounting/unmounting with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Trigger animation on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      const timeout = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  // Close drawer on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!shouldRender) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-[550px] bg-white shadow-xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
          isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 h-[60px] flex items-center justify-between px-4 border-b border-default-200">
          <h2 className="text-lg font-semibold text-slate-800">
            Recherches
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* New conversation button */}
        <div className="flex-shrink-0 p-4 border-b border-slate-200">
          <Button
            variant="primary"
            size="sm"
            icon={Plus}
            onClick={() => {
              onNewConversation();
              onClose();
            }}
            className="w-full"
          >
            Nouvelle recherche
          </Button>
        </div>

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">
              Aucune recherche
            </div>
          ) : (
            conversations.map((conversation) => {
              const displayLabel = conversation.title || conversation.label || "Sans titre";
              const date = new Date(conversation.created_at);
              const formattedDate = date.toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "short",
                year: "numeric",
              });
              const formattedTime = date.toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit",
              });

              return (
                <SidebarNavItem
                  key={conversation.id}
                  label={displayLabel}
                  sublabel={`${formattedDate} à ${formattedTime}`}
                  isActive={conversation.id === currentConversationId}
                  onClick={() => {
                    onSelectConversation(conversation.id);
                    onClose();
                  }}
                />
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
